import { useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { Button } from 'components/v2/Button'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { getDateRange, sanitizeDateRange } from 'features/App/components/Filters/DateRangeFilter'
import { CONCILIATION_STATUS } from 'features/Inventories/components/ConciliationStatus/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { TicketsInsuranceList } from './List'
import { TicketsInsurancePagination } from './Pagination'
import { TicketsInsuranceFilters, HAS_CONFLICT_LIST } from './Filters'
import { DEFAULT_TIMEZONE } from 'utils/date'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const TICKETS_INSURANCE_LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'ticketsInsurance.helmet',
    defaultMessage: 'Tickets insurance | Nexbus Admin'
  }
})

const GET_TICKETS_INSURANCE = gql`
  query GetTicketsInsurance(
    $page: Int!
    $limit: Int!
    $reservation: ID
    $status: [AncillaryStatus!]
    $travelDateRange: dateRangeInput
    $timezone: String
    $hasConflict: Boolean
    $certificateIDs: [String!]
  ) {
    ticketsInsurance(
      pagination: { size: $limit, page: $page }
      input: {
        bookingID: $reservation
        status: $status
        travelDateRange: $travelDateRange
        timezone: $timezone
        hasConflict: $hasConflict
        certificateIDs: $certificateIDs
      }
    ) {
      total
      items {
        id
        bookingID
        trip {
          id
          segment {
            id
            departureAt
            origin {
              id
              name
              city {
                timezone
              }
            }
            destination {
              id
              name
            }
          }
        }
        passenger {
          firstName
          lastName
          type
        }
        insurance {
          status
          confirmedAt
          externalID
          certificateID
          insuranceCoverage {
            departureAt
            arrivalAt
            passenger {
              firstName
              lastName
              email
              phone
            }
          }
        }
        hasConflict
      }
    }
  }
`

const getHasConflict = (hasConflict: TSFixMe): TSFixMe => {
  const hasConflictIndex = HAS_CONFLICT_LIST.findIndex((c): TSFixMe => c === hasConflict)
  let result = null
  if (hasConflictIndex !== -1) {
    result = hasConflict === CONCILIATION_STATUS.CONFLICTING
  }
  return result
}

const TicketsInsuranceView = (): TSFixMe => {
  const location = useLocation()
  const intl = useIntl()

  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.BOOKING, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BOOKING} />
  }

  let content
  let {
    page = 1,
    // eslint-disable-next-line prefer-const
    reservation = '',
    status = '',
    // eslint-disable-next-line prefer-const
    travelDateFrom,
    // eslint-disable-next-line prefer-const
    travelDateTo,
    // eslint-disable-next-line prefer-const
    travelDateRangeValue,
    // eslint-disable-next-line prefer-const
    hasConflict = '',
    certificateIDs = ''
  } = getQueries(location.search)

  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  status = status ? status.split(',') : []
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  certificateIDs = certificateIDs ? certificateIDs.split(',') : []
  const sanitizedTravelDateRange = sanitizeDateRange({
    from: travelDateFrom,
    to: travelDateTo,
    rangeValue: travelDateRangeValue
  })
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  page = parseInt(page)

  const variables = filterEmptyValue({
    page: page - 1,
    limit: TICKETS_INSURANCE_LIMIT,
    reservation,
    status,
    travelDateRange: getDateRange(sanitizedTravelDateRange),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE,
    certificateIDs
  })

  const sanetizeHasConflict = getHasConflict(hasConflict)
  if (typeof sanetizeHasConflict === 'boolean') {
    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    variables.hasConflict = sanetizeHasConflict
  }

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery(GET_TICKETS_INSURANCE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables
  })

  const filters = {
    reservation,
    status,
    travelDateRange: sanitizedTravelDateRange,
    hasConflict,
    certificateIDs
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="ticketsInsurance.error.description"
            defaultMessage="There was an error while loading your tickets with insurance"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="ticketsInsurance.error.action" defaultMessage="Reload your tickets with insurance" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.ticketsInsurance.items) && page === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="ticketsInsurance.empty.description"
            defaultMessage="You don&#39;t have any tickets with insurance yet"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.ticketsInsurance.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="ticketsInsurance.empty.filters.description"
            defaultMessage="There was no tickets with insurance found according to your filters"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/ancillaries/insurance" kind="primary">
            <FormattedMessage id="ticketsInsurance.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <TicketsInsuranceList ticketsInsurance={data.ticketsInsurance.items} />

        <TicketsInsurancePagination
          page={page}
          totalItems={data.ticketsInsurance.total}
          limit={TICKETS_INSURANCE_LIMIT}
          currentItemsCount={data.ticketsInsurance.items.length}
          filters={filters}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="ticketsInsurance.header.label" defaultMessage="Tickets insurance" />
        </Heading.H3>
      </ContentHeader>
      <ContentBody>
        {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
        <TicketsInsuranceFilters filters={filters} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default TicketsInsuranceView
